import { useDispatch, useSelector } from "react-redux";
import {type RootState } from "../../../../../store";
import type IFiltersNew from "../../../../../types/IFiltersNew";
import { formatDateStringToString, formatDateToString } from "../../../../../utils/DateFormatting";
import { search } from "../../../constructionsNewSlice";

interface MultiSelectChange {
  key: string;
  selected: boolean;
}

type FilterValue<T extends keyof IFiltersNew> = 
  IFiltersNew[T] extends string[] ? MultiSelectChange: IFiltersNew[T];

const useConstructionsNewFiltersHook = (): {
  filters: IFiltersNew;
  handleFilterChange: <T extends keyof IFiltersNew>(value: FilterValue<T>, name: T) => void;
  handleFiltersClear: () => void;
  handleFormSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  handleFormattedDate: (date?: Date) => string;
} => {
  const filters = useSelector((state: RootState) => state.constructionsNew.filters);
  const dispatch = useDispatch();

  const handleFilterChange = <T extends keyof IFiltersNew>(
     value: FilterValue<T>,
     name: T
  ): void => {
    const currentValue = filters[name];

    if (Array.isArray(currentValue) && (value as MultiSelectChange)?.key) {
      const multiSelectValue = value as MultiSelectChange;
      const updatedValues = multiSelectValue.selected
        ? [...currentValue, multiSelectValue.key] 
        : currentValue.filter((v) => v !== multiSelectValue.key);

      const updatedMultiSelect: IFiltersNew = {...filters, [name]: updatedValues};
      dispatch(search(updatedMultiSelect));
      return;
    }

    const updatedFilter: IFiltersNew = {...filters, [name]: value};
    dispatch(search(updatedFilter));
   };

  const handleFiltersClear = (): void => {
    const emptyFilters: IFiltersNew = {
      name: undefined,
      from: undefined,
      to: undefined,
      locationCodes: [],
      tags: [],
      createdBy: [],
    };
    dispatch(search(emptyFilters));
  };

  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    const updatedFormFilters: IFiltersNew = {
        ...filters,
        from: formatDateStringToString(filters.from),
        to: formatDateStringToString(filters.to),
      };
    dispatch(search(updatedFormFilters));
  };

  const handleFormattedDate = (date?: Date): string => formatDateToString(date);

  return {
    filters,
    handleFilterChange,
    handleFiltersClear,
    handleFormSubmit,
    handleFormattedDate,
  };
};

export default useConstructionsNewFiltersHook;